/**
 * One summary row.
 *
 * Memoised deliberately. The charge loop dispatches a result per row, and a
 * realistic batch is 50 to 100 rows, so an unmemoised table would re-render
 * every row on every update. That is thousands of renders during the one
 * operation where the UI must stay responsive.
 *
 * Charity and event are not columns here: they live on the group header, which
 * is the only place they are not repeated identically down the table.
 */

import React from 'react';
import { Button, Table } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import CurrencyHelper from '../../../../helpers/currencyHelper';
import { formatDateByCountry } from '../../../../helpers/dateAndTimeHelper';
import { eligibilityVerdict, i18nKeys } from '../constants';
import { ambassadorCurrency, fullName } from '../helpers';
import { RecentChargeEntry, SummaryRow } from '../types';
import EligibilityBadge from './EligibilityBadge';
import RowStatusCell from './RowStatusCell';
import styles from '../BulkChargeGuarantee.module.css';

interface SummaryTableRowProps {
  row: SummaryRow;
  recentEntries: RecentChargeEntry[];
  now: number;
  canRemove: boolean;
  /** Outcomes are meaningless until a run has started, so stay blank. */
  showOutcome: boolean;
  onRemoveId: (id: number) => void;
  onRefreshId: (id: number) => void;
}

const NotAvailable: React.FC = () => (
  <span className={styles.amountUnavailable}>{I18n.t(i18nKeys.TABLE.NOT_AVAILABLE)}</span>
);

const AmountCell: React.FC<{ row: SummaryRow }> = ({ row }) => {
  if (row.amount.amount === null) {
    return (
      <span className={styles.amountUnavailable}>
        {I18n.t(i18nKeys.TABLE.AMOUNT_UNAVAILABLE)}
      </span>
    );
  }
  return (
    <>
      {CurrencyHelper.formatCurrency(row.amount.amount, ambassadorCurrency(row.ambassador), {
        alwaysShowDecimals: true
      })}
    </>
  );
};

const SummaryTableRow: React.FC<SummaryTableRowProps> = ({
  row,
  recentEntries,
  now,
  canRemove,
  showOutcome,
  onRemoveId,
  onRefreshId
}) => {
  const ambassador = row.ambassador;
  const name = fullName(row);

  return (
    <Table.Row
      className={
        row.eligibility.verdict === eligibilityVerdict.blocked ? styles.blockedRow : undefined
      }
    >
      <Table.Cell>{row.id}</Table.Cell>
      <Table.Cell>{name || <NotAvailable />}</Table.Cell>
      <Table.Cell className={styles.emailCell} title={ambassador?.email || undefined}>
        {ambassador?.email || <NotAvailable />}
      </Table.Cell>
      <Table.Cell>
        {ambassador?.fundraiseBy ? (
          // The helper falls back to its default format on an empty country.
          formatDateByCountry(ambassador.fundraiseBy, ambassador.organization?.country || '')
        ) : (
          <NotAvailable />
        )}
      </Table.Cell>
      <Table.Cell className={styles.amountCell}>
        <AmountCell row={row} />
      </Table.Cell>
      <Table.Cell>
        <EligibilityBadge
          ambassadorId={row.id}
          eligibility={row.eligibility}
          recentEntries={recentEntries}
          now={now}
        />
      </Table.Cell>
      <Table.Cell>{showOutcome ? <RowStatusCell row={row} /> : null}</Table.Cell>
      <Table.Cell className={styles.actionsCell}>
        {canRemove && (
          <Button
            icon="close"
            circular
            compact
            size="mini"
            aria-label={I18n.t(i18nKeys.TABLE.REMOVE)}
            title={I18n.t(i18nKeys.TABLE.REMOVE)}
            onClick={() => onRemoveId(row.id)}
          />
        )}
        <Button
          icon="refresh"
          circular
          compact
          size="mini"
          loading={row.isRefreshing}
          disabled={row.isRefreshing}
          aria-label={I18n.t(i18nKeys.TABLE.REFRESH)}
          title={I18n.t(i18nKeys.TABLE.REFRESH)}
          onClick={() => onRefreshId(row.id)}
        />
      </Table.Cell>
    </Table.Row>
  );
};

export default React.memo(SummaryTableRow);
